import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import { toast } from "sonner";
import { 
  Package, 
  Search, 
  Edit2, 
  Trash2,
  AlertTriangle,
  RefreshCw,
  ShoppingCart,
  Filter,
  X,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  History,
  TrendingUp,
  TrendingDown,
  FileText,
  Calendar,
  DollarSign,
  ChevronRight,
  ChevronDown,
  Layers
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Função para extrair o nome base do produto (sem sabor/variação)
const getProductBaseName = (name) => {
  if (!name) return '';
  
  // Lista de palavras que indicam variações/sabores
  const variationWords = [
    'sabor', 'morango', 'chocolate', 'baunilha', 'limao', 'laranja', 'uva',
    'menta', 'hortela', 'tutti', 'frutti', 'original', 'zero', 'light',
    'diet', 'sem', 'acucar', 'tradicional', 'especial', 'extra', 'forte',
    'suave', 'intenso', 'fresh', 'ice', 'cool', 'mint', 'berry', 'citrus',
    'tropical', 'melancia', 'abacaxi', 'manga', 'pessego', 'cereja', 'coco',
    'amendoim', 'avela', 'caramelo', 'doce', 'leite', 'branco', 'preto',
    'vermelho', 'verde', 'azul', 'amarelo', 'rosa', 'roxo'
  ];
  
  const words = name.toLowerCase().split(/[\s\-\_\/]+/);
  const baseWords = [];
  
  for (let i = 0; i < Math.min(words.length, 3); i++) {
    const word = words[i].replace(/[^a-z0-9]/g, '');
    if (word.length >= 2 && !variationWords.includes(word)) {
      baseWords.push(word);
    }
    // Parar se encontrar um número (geralmente indica tamanho/peso)
    if (/^\d+/.test(words[i])) break;
  }
  
  return baseWords.slice(0, 2).join(' ') || words[0] || '';
};

const Stock = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editProduct, setEditProduct] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [processing, setProcessing] = useState(false);
  
  // Histórico
  const [historyProduct, setHistoryProduct] = useState(null);
  const [historyData, setHistoryData] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(false);
  
  // Agrupamento
  const [groupByProduct, setGroupByProduct] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  
  // Filtros por coluna
  const [filters, setFilters] = useState({
    ean: "",
    name: "",
    stockStatus: "all",
    minCostPrice: "",
    maxCostPrice: "",
    minSalePrice: "",
    maxSalePrice: "",
    minMarkup: "",
    maxMarkup: ""
  });
  
  // Ordenação
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  
  // Mostrar/esconder filtros avançados
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/products`);
      setProducts(res.data);
    } catch (error) {
      console.error("Error fetching products:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProductHistory = async (product) => {
    setHistoryProduct(product);
    setLoadingHistory(true);
    setHistoryData(null);
    
    try {
      const res = await axios.get(`${API}/products/${product.ean}/history`);
      setHistoryData(res.data);
    } catch (error) {
      console.error("Error fetching history:", error);
      toast.error("Erro ao carregar histórico");
    } finally {
      setLoadingHistory(false);
    }
  };

  // Calcular valor total em estoque
  const stockValue = useMemo(() => {
    const productsWithStock = products.filter(p => p.quantity > 0);
    const totalCostValue = productsWithStock.reduce((sum, p) => sum + (p.quantity * (p.cost_price || 0)), 0);
    const totalSaleValue = productsWithStock.reduce((sum, p) => sum + (p.quantity * (p.sale_price || 0)), 0);
    const totalUnits = productsWithStock.reduce((sum, p) => sum + p.quantity, 0);
    return {
      costValue: totalCostValue,
      saleValue: totalSaleValue,
      units: totalUnits,
      productsCount: productsWithStock.length
    };
  }, [products]);

  // Filtragem e ordenação
  const filteredProducts = useMemo(() => {
    let result = [...products];
    
    if (filters.ean) {
      result = result.filter(p => p.ean?.toLowerCase().includes(filters.ean.toLowerCase()));
    }
    
    if (filters.name) {
      result = result.filter(p => p.name?.toLowerCase().includes(filters.name.toLowerCase()));
    }
    
    if (filters.stockStatus !== "all") {
      if (filters.stockStatus === "zero") {
        result = result.filter(p => p.quantity === 0);
      } else if (filters.stockStatus === "low") {
        result = result.filter(p => p.quantity > 0 && p.quantity <= 5);
      } else if (filters.stockStatus === "negative") {
        result = result.filter(p => p.quantity < 0);
      } else if (filters.stockStatus === "normal") {
        result = result.filter(p => p.quantity > 5);
      }
    }
    
    if (filters.minCostPrice) {
      result = result.filter(p => (p.cost_price || 0) >= parseFloat(filters.minCostPrice));
    }
    if (filters.maxCostPrice) {
      result = result.filter(p => (p.cost_price || 0) <= parseFloat(filters.maxCostPrice));
    }
    
    if (filters.minSalePrice) {
      result = result.filter(p => (p.sale_price || 0) >= parseFloat(filters.minSalePrice));
    }
    if (filters.maxSalePrice) {
      result = result.filter(p => (p.sale_price || 0) <= parseFloat(filters.maxSalePrice));
    }
    
    if (filters.minMarkup) {
      result = result.filter(p => (p.markup || 0) >= parseFloat(filters.minMarkup));
    }
    if (filters.maxMarkup) {
      result = result.filter(p => (p.markup || 0) <= parseFloat(filters.maxMarkup));
    }
    
    if (sortConfig.key) {
      result.sort((a, b) => {
        let aVal = a[sortConfig.key];
        let bVal = b[sortConfig.key];
        
        if (aVal == null) aVal = sortConfig.key === 'name' || sortConfig.key === 'ean' ? '' : 0;
        if (bVal == null) bVal = sortConfig.key === 'name' || sortConfig.key === 'ean' ? '' : 0;
        
        if (typeof aVal === 'string') {
          aVal = aVal.toLowerCase();
          bVal = bVal.toLowerCase();
        }
        
        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    
    return result;
  }, [products, filters, sortConfig]);

  // Agrupamento de produtos
  const groupedProducts = useMemo(() => {
    if (!groupByProduct) return null;
    
    const groups = {};
    filteredProducts.forEach(product => {
      const baseName = getProductBaseName(product.name);
      if (!groups[baseName]) {
        groups[baseName] = {
          name: baseName,
          products: [],
          totalQuantity: 0,
          totalCostValue: 0,
          totalSaleValue: 0
        };
      }
      groups[baseName].products.push(product);
      groups[baseName].totalQuantity += product.quantity || 0;
      groups[baseName].totalCostValue += (product.quantity || 0) * (product.cost_price || 0);
      groups[baseName].totalSaleValue += (product.quantity || 0) * (product.sale_price || 0);
    });
    
    // Ordenar grupos por nome e converter para array
    return Object.values(groups)
      .filter(g => g.products.length > 0)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [filteredProducts, groupByProduct]);

  const toggleGroup = (groupName) => {
    setExpandedGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(groupName)) {
        newSet.delete(groupName);
      } else {
        newSet.add(groupName);
      }
      return newSet;
    });
  };

  const expandAllGroups = () => {
    if (groupedProducts) {
      setExpandedGroups(new Set(groupedProducts.map(g => g.name)));
    }
  };

  const collapseAllGroups = () => {
    setExpandedGroups(new Set());
  };

  const handleSort = (key) => {
    setSortConfig(current => ({
      key,
      direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <ArrowUpDown className="w-3 h-3 ml-1 opacity-30" />;
    return sortConfig.direction === 'asc' 
      ? <ArrowUp className="w-3 h-3 ml-1" /> 
      : <ArrowDown className="w-3 h-3 ml-1" />;
  };

  const clearFilters = () => {
    setFilters({
      ean: "",
      name: "",
      stockStatus: "all",
      minCostPrice: "",
      maxCostPrice: "",
      minSalePrice: "",
      maxSalePrice: "",
      minMarkup: "",
      maxMarkup: ""
    });
    setSortConfig({ key: null, direction: 'asc' });
  };

  const hasActiveFilters = () => {
    return filters.ean || filters.name || filters.stockStatus !== "all" ||
           filters.minCostPrice || filters.maxCostPrice ||
           filters.minSalePrice || filters.maxSalePrice ||
           filters.minMarkup || filters.maxMarkup;
  };

  const handleProcessSales = async () => {
    if (!window.confirm("Processar vendas e dar baixa no estoque?\n\nApenas vendas a partir de 16/08/2025 serão processadas.")) {
      return;
    }

    setProcessing(true);
    try {
      const res = await axios.post(`${API}/stock/process-sales`);
      toast.success("Estoque atualizado!", {
        description: `${res.data.products_updated} produtos atualizados. ${res.data.total_items_deducted} itens deduzidos. (${res.data.sales_ignored || 0} vendas anteriores ignoradas)`
      });
      fetchProducts();
    } catch (error) {
      toast.error("Erro ao processar", {
        description: error.response?.data?.detail || "Tente novamente"
      });
    } finally {
      setProcessing(false);
    }
  };

  const handleEdit = (product, e) => {
    e.stopPropagation();
    setEditProduct(product);
    setEditForm({
      name: product.name,
      quantity: product.quantity,
      cost_price: product.cost_price,
      sale_price: product.sale_price
    });
  };

  const handleSaveEdit = async () => {
    try {
      await axios.put(`${API}/products/${editProduct.id}`, editForm);
      toast.success("Produto atualizado!");
      setEditProduct(null);
      fetchProducts();
    } catch (error) {
      toast.error("Erro ao atualizar", {
        description: error.response?.data?.detail || "Tente novamente"
      });
    }
  };

  const handleDelete = async (productId, e) => {
    e.stopPropagation();
    if (!window.confirm("Tem certeza que deseja excluir este produto?")) return;
    
    try {
      await axios.delete(`${API}/products/${productId}`);
      toast.success("Produto excluído!");
      fetchProducts();
    } catch (error) {
      toast.error("Erro ao excluir", {
        description: error.response?.data?.detail || "Tente novamente"
      });
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return date.toLocaleDateString('pt-BR');
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return date.toLocaleString('pt-BR');
  };

  const getStockBadge = (quantity) => {
    if (quantity < 0) {
      return <Badge variant="destructive" className="text-xs">NEGATIVO ({quantity})</Badge>;
    } else if (quantity === 0) {
      return <Badge variant="destructive" className="text-xs">ZERADO</Badge>;
    } else if (quantity <= 5) {
      return <Badge className="text-xs bg-amber-100 text-amber-700 hover:bg-amber-100">BAIXO ({quantity})</Badge>;
    }
    return <Badge variant="secondary" className="text-xs">{quantity} un</Badge>;
  };

  return (
    <div className="p-8 animate-fadeIn" data-testid="stock-page">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Estoque</h1>
          <p className="text-muted-foreground">Gerencie os produtos do seu mercado</p>
        </div>
        <div className="flex items-center gap-4">
          <Button
            onClick={handleProcessSales}
            disabled={processing || products.length === 0}
            variant="outline"
            data-testid="process-sales-btn"
          >
            {processing ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <ShoppingCart className="w-4 h-4 mr-2" />
            )}
            Processar Vendas
          </Button>
          <div className="text-right">
            <p className="text-2xl font-bold font-mono">{products.length}</p>
            <p className="text-sm text-muted-foreground">produtos cadastrados</p>
          </div>
        </div>
      </div>

      {/* Valor do Estoque */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card className="bg-green-50 border-green-200">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-green-700 font-medium">Valor em Estoque (Custo)</p>
                <p className="text-2xl font-bold font-mono text-green-800">{formatCurrency(stockValue.costValue)}</p>
              </div>
              <Package className="w-8 h-8 text-green-600 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-blue-700 font-medium">Valor em Estoque (Venda)</p>
                <p className="text-2xl font-bold font-mono text-blue-800">{formatCurrency(stockValue.saleValue)}</p>
              </div>
              <DollarSign className="w-8 h-8 text-blue-600 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-purple-50 border-purple-200">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-purple-700 font-medium">Total de Unidades</p>
                <p className="text-2xl font-bold font-mono text-purple-800">{stockValue.units.toLocaleString('pt-BR')}</p>
              </div>
              <Package className="w-8 h-8 text-purple-600 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-amber-700 font-medium">Produtos com Estoque</p>
                <p className="text-2xl font-bold font-mono text-amber-800">{stockValue.productsCount}</p>
              </div>
              <Package className="w-8 h-8 text-amber-600 opacity-50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filtros */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="flex items-center gap-4 mb-4">
            <Button
              variant={showFilters ? "default" : "outline"}
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
              data-testid="toggle-filters-btn"
            >
              <Filter className="w-4 h-4 mr-2" />
              Filtros
              {hasActiveFilters() && (
                <Badge variant="secondary" className="ml-2 bg-white text-primary">
                  Ativos
                </Badge>
              )}
            </Button>
            
            {hasActiveFilters() && (
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                <X className="w-4 h-4 mr-1" />
                Limpar filtros
              </Button>
            )}

            {/* Agrupamento Toggle */}
            <div className="flex items-center gap-2 ml-4 pl-4 border-l">
              <Layers className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">Agrupar similares</span>
              <Switch
                checked={groupByProduct}
                onCheckedChange={setGroupByProduct}
                data-testid="group-products-switch"
              />
            </div>
            
            {groupByProduct && groupedProducts && (
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="sm" onClick={expandAllGroups}>
                  Expandir todos
                </Button>
                <Button variant="ghost" size="sm" onClick={collapseAllGroups}>
                  Colapsar todos
                </Button>
              </div>
            )}
            
            <div className="ml-auto text-sm text-muted-foreground">
              {groupByProduct && groupedProducts ? (
                <span>{groupedProducts.length} grupos ({filteredProducts.length} produtos)</span>
              ) : (
                <span>Exibindo {filteredProducts.length} de {products.length} produtos</span>
              )}
            </div>
          </div>

          {showFilters && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 pt-4 border-t">
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1 block">EAN</label>
                <Input
                  value={filters.ean}
                  onChange={(e) => setFilters({...filters, ean: e.target.value})}
                  placeholder="Filtrar por EAN..."
                  className="h-9"
                  data-testid="filter-ean"
                />
              </div>
              
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1 block">Produto</label>
                <Input
                  value={filters.name}
                  onChange={(e) => setFilters({...filters, name: e.target.value})}
                  placeholder="Filtrar por nome..."
                  className="h-9"
                  data-testid="filter-name"
                />
              </div>
              
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1 block">Status Estoque</label>
                <Select
                  value={filters.stockStatus}
                  onValueChange={(value) => setFilters({...filters, stockStatus: value})}
                >
                  <SelectTrigger className="h-9" data-testid="filter-stock-status">
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="negative">Negativo</SelectItem>
                    <SelectItem value="zero">Zerado</SelectItem>
                    <SelectItem value="low">Baixo (≤5)</SelectItem>
                    <SelectItem value="normal">Normal (&gt;5)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1 block">Preço Custo (R$)</label>
                <div className="flex gap-2">
                  <Input
                    type="number"
                    step="0.01"
                    value={filters.minCostPrice}
                    onChange={(e) => setFilters({...filters, minCostPrice: e.target.value})}
                    placeholder="Mín"
                    className="h-9"
                  />
                  <Input
                    type="number"
                    step="0.01"
                    value={filters.maxCostPrice}
                    onChange={(e) => setFilters({...filters, maxCostPrice: e.target.value})}
                    placeholder="Máx"
                    className="h-9"
                  />
                </div>
              </div>
              
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1 block">Preço Venda (R$)</label>
                <div className="flex gap-2">
                  <Input
                    type="number"
                    step="0.01"
                    value={filters.minSalePrice}
                    onChange={(e) => setFilters({...filters, minSalePrice: e.target.value})}
                    placeholder="Mín"
                    className="h-9"
                  />
                  <Input
                    type="number"
                    step="0.01"
                    value={filters.maxSalePrice}
                    onChange={(e) => setFilters({...filters, maxSalePrice: e.target.value})}
                    placeholder="Máx"
                    className="h-9"
                  />
                </div>
              </div>
              
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1 block">Markup (%)</label>
                <div className="flex gap-2">
                  <Input
                    type="number"
                    step="0.1"
                    value={filters.minMarkup}
                    onChange={(e) => setFilters({...filters, minMarkup: e.target.value})}
                    placeholder="Mín"
                    className="h-9"
                  />
                  <Input
                    type="number"
                    step="0.1"
                    value={filters.maxMarkup}
                    onChange={(e) => setFilters({...filters, maxMarkup: e.target.value})}
                    placeholder="Máx"
                    className="h-9"
                  />
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Products Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Package className="w-5 h-5" strokeWidth={1.5} />
            {groupByProduct ? `Grupos (${groupedProducts?.length || 0})` : `Produtos (${filteredProducts.length})`}
            <span className="text-xs font-normal text-muted-foreground ml-2">
              Clique em um produto para ver o histórico
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-8 text-center text-muted-foreground">Carregando...</div>
          ) : groupByProduct && groupedProducts ? (
            /* Visão Agrupada */
            <div className="space-y-2" data-testid="grouped-products-view">
              {groupedProducts.length > 0 ? groupedProducts.map((group) => (
                <div 
                  key={group.name} 
                  className="border rounded-lg overflow-hidden"
                  data-testid={`product-group-${group.name}`}
                >
                  {/* Header do Grupo */}
                  <div 
                    className="flex items-center gap-3 p-3 bg-muted/50 cursor-pointer hover:bg-muted/70 transition-colors"
                    onClick={() => toggleGroup(group.name)}
                  >
                    {expandedGroups.has(group.name) ? (
                      <ChevronDown className="w-5 h-5 text-muted-foreground" />
                    ) : (
                      <ChevronRight className="w-5 h-5 text-muted-foreground" />
                    )}
                    <div className="flex-1">
                      <span className="font-semibold uppercase">{group.name}</span>
                      <Badge variant="secondary" className="ml-2">
                        {group.products.length} {group.products.length === 1 ? 'variação' : 'variações'}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-6 text-sm">
                      <div className="text-right">
                        <span className="text-muted-foreground">Estoque:</span>
                        <span className={`ml-2 font-mono font-medium ${
                          group.totalQuantity < 0 ? 'text-red-600' :
                          group.totalQuantity === 0 ? 'text-amber-600' : 'text-green-600'
                        }`}>
                          {group.totalQuantity} un
                        </span>
                      </div>
                      <div className="text-right">
                        <span className="text-muted-foreground">Valor:</span>
                        <span className="ml-2 font-mono font-medium">{formatCurrency(group.totalSaleValue)}</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Conteúdo do Grupo (produtos) */}
                  {expandedGroups.has(group.name) && (
                    <div className="border-t">
                      <table className="data-table" data-testid={`group-table-${group.name}`}>
                        <thead>
                          <tr>
                            <th>EAN</th>
                            <th>Produto</th>
                            <th>Estoque</th>
                            <th>Preço Custo</th>
                            <th>Preço Venda</th>
                            <th>Markup</th>
                            <th>Ações</th>
                          </tr>
                        </thead>
                        <tbody>
                          {group.products.map((product) => (
                            <tr 
                              key={product.id} 
                              className={`cursor-pointer hover:bg-muted/30 transition-colors ${
                                product.quantity < 0 ? 'bg-red-100 hover:bg-red-200' :
                                product.quantity === 0 ? 'bg-destructive/5 hover:bg-destructive/10' : ''
                              }`}
                              onClick={() => fetchProductHistory(product)}
                              data-testid={`product-row-${product.ean}`}
                            >
                              <td className="font-mono text-xs">{product.ean}</td>
                              <td className="max-w-[250px] truncate" title={product.name}>
                                {product.quantity <= 0 && (
                                  <AlertTriangle className="w-4 h-4 text-destructive inline mr-2" />
                                )}
                                {product.name}
                              </td>
                              <td>{getStockBadge(product.quantity)}</td>
                              <td className="font-mono">{formatCurrency(product.cost_price)}</td>
                              <td className="font-mono font-medium">{formatCurrency(product.sale_price)}</td>
                              <td className="font-mono text-sm">
                                {product.markup ? `${product.markup.toFixed(1)}%` : '-'}
                              </td>
                              <td>
                                <div className="flex gap-1">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={(e) => handleEdit(product, e)}
                                    data-testid={`edit-${product.id}`}
                                  >
                                    <Edit2 className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={(e) => handleDelete(product.id, e)}
                                    className="text-destructive hover:text-destructive"
                                    data-testid={`delete-${product.id}`}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )) : (
                <div className="py-8 text-center text-muted-foreground">
                  <Package className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>Nenhum grupo encontrado</p>
                </div>
              )}
            </div>
          ) : filteredProducts.length > 0 ? (
            /* Visão Normal (sem agrupamento) */
            <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
              <table className="data-table" data-testid="products-table">
                <thead className="sticky top-0 bg-white z-10">
                  <tr>
                    <th 
                      className="cursor-pointer hover:bg-muted/50 select-none"
                      onClick={() => handleSort('ean')}
                    >
                      <div className="flex items-center">
                        EAN {getSortIcon('ean')}
                      </div>
                    </th>
                    <th 
                      className="cursor-pointer hover:bg-muted/50 select-none"
                      onClick={() => handleSort('name')}
                    >
                      <div className="flex items-center">
                        Produto {getSortIcon('name')}
                      </div>
                    </th>
                    <th 
                      className="cursor-pointer hover:bg-muted/50 select-none"
                      onClick={() => handleSort('quantity')}
                    >
                      <div className="flex items-center">
                        Estoque {getSortIcon('quantity')}
                      </div>
                    </th>
                    <th 
                      className="cursor-pointer hover:bg-muted/50 select-none"
                      onClick={() => handleSort('cost_price')}
                    >
                      <div className="flex items-center">
                        Preço Custo {getSortIcon('cost_price')}
                      </div>
                    </th>
                    <th 
                      className="cursor-pointer hover:bg-muted/50 select-none"
                      onClick={() => handleSort('sale_price')}
                    >
                      <div className="flex items-center">
                        Preço Venda {getSortIcon('sale_price')}
                      </div>
                    </th>
                    <th 
                      className="cursor-pointer hover:bg-muted/50 select-none"
                      onClick={() => handleSort('markup')}
                    >
                      <div className="flex items-center">
                        Markup {getSortIcon('markup')}
                      </div>
                    </th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProducts.map((product) => (
                    <tr 
                      key={product.id} 
                      className={`cursor-pointer hover:bg-muted/30 transition-colors ${
                        product.quantity < 0 ? 'bg-red-100 hover:bg-red-200' :
                        product.quantity === 0 ? 'bg-destructive/5 hover:bg-destructive/10' : ''
                      }`}
                      onClick={() => fetchProductHistory(product)}
                      data-testid={`product-row-${product.ean}`}
                    >
                      <td className="font-mono text-xs">{product.ean}</td>
                      <td className="max-w-[250px] truncate" title={product.name}>
                        {product.quantity <= 0 && (
                          <AlertTriangle className="w-4 h-4 text-destructive inline mr-2" />
                        )}
                        {product.name}
                      </td>
                      <td>{getStockBadge(product.quantity)}</td>
                      <td className="font-mono">{formatCurrency(product.cost_price)}</td>
                      <td className="font-mono font-medium">{formatCurrency(product.sale_price)}</td>
                      <td className="font-mono text-sm">
                        {product.markup ? `${product.markup.toFixed(1)}%` : '-'}
                      </td>
                      <td>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => handleEdit(product, e)}
                            data-testid={`edit-${product.id}`}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => handleDelete(product.id, e)}
                            className="text-destructive hover:text-destructive"
                            data-testid={`delete-${product.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="py-8 text-center text-muted-foreground">
              <Package className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>Nenhum produto encontrado</p>
              {hasActiveFilters() ? (
                <p className="text-sm">Tente ajustar os filtros</p>
              ) : (
                <p className="text-sm">Importe uma nota fiscal para adicionar produtos</p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* History Dialog */}
      <Dialog open={!!historyProduct} onOpenChange={() => { setHistoryProduct(null); setHistoryData(null); }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5" />
              Histórico do Produto
            </DialogTitle>
          </DialogHeader>
          
          {loadingHistory ? (
            <div className="py-8 text-center text-muted-foreground">
              <RefreshCw className="w-8 h-8 mx-auto mb-2 animate-spin opacity-50" />
              Carregando histórico...
            </div>
          ) : historyData ? (
            <div className="flex-1 overflow-hidden flex flex-col">
              {/* Product Info */}
              <div className="bg-muted/50 rounded-sm p-4 mb-4">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-semibold text-lg">{historyData.product?.name}</h3>
                    <p className="text-sm text-muted-foreground font-mono">EAN: {historyData.product?.ean}</p>
                  </div>
                  <div className="text-right">
                    {getStockBadge(historyData.product?.quantity)}
                    <p className="text-xs text-muted-foreground mt-1">Estoque atual</p>
                  </div>
                </div>
              </div>

              {/* Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
                <div className="bg-green-50 border border-green-200 rounded-sm p-3">
                  <div className="flex items-center gap-2 text-green-700 mb-1">
                    <TrendingUp className="w-4 h-4" />
                    <span className="text-xs font-medium">Entradas</span>
                  </div>
                  <p className="text-xl font-bold text-green-800">+{historyData.summary?.total_purchased || 0}</p>
                </div>
                <div className="bg-red-50 border border-red-200 rounded-sm p-3">
                  <div className="flex items-center gap-2 text-red-700 mb-1">
                    <TrendingDown className="w-4 h-4" />
                    <span className="text-xs font-medium">Saídas</span>
                  </div>
                  <p className="text-xl font-bold text-red-800">-{historyData.summary?.total_sold || 0}</p>
                </div>
                <div className={`border rounded-sm p-3 ${
                  historyData.summary?.current_stock < 0 
                    ? 'bg-red-100 border-red-300' 
                    : historyData.summary?.current_stock === 0 
                      ? 'bg-amber-50 border-amber-200'
                      : 'bg-blue-50 border-blue-200'
                }`}>
                  <div className={`flex items-center gap-2 mb-1 ${
                    historyData.summary?.current_stock < 0 ? 'text-red-700' : 
                    historyData.summary?.current_stock === 0 ? 'text-amber-700' : 'text-blue-700'
                  }`}>
                    <Package className="w-4 h-4" />
                    <span className="text-xs font-medium">Saldo</span>
                  </div>
                  <p className={`text-xl font-bold ${
                    historyData.summary?.current_stock < 0 ? 'text-red-800' : 
                    historyData.summary?.current_stock === 0 ? 'text-amber-800' : 'text-blue-800'
                  }`}>
                    ={historyData.summary?.current_stock || 0}
                  </p>
                </div>
                <div className="bg-purple-50 border border-purple-200 rounded-sm p-3">
                  <div className="flex items-center gap-2 text-purple-700 mb-1">
                    <ShoppingCart className="w-4 h-4" />
                    <span className="text-xs font-medium">Receita</span>
                  </div>
                  <p className="text-lg font-bold text-purple-800">{formatCurrency(historyData.summary?.total_revenue)}</p>
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded-sm p-3">
                  <div className="flex items-center gap-2 text-amber-700 mb-1">
                    <Calendar className="w-4 h-4" />
                    <span className="text-xs font-medium">Média/Dia</span>
                  </div>
                  <p className="text-xl font-bold text-amber-800">{historyData.summary?.avg_daily_sales || 0}</p>
                </div>
              </div>

              {/* Stock Balance Equation */}
              <div className="bg-muted/30 rounded-sm p-3 mb-4 flex items-center justify-center gap-4 text-sm">
                <span className="font-mono">
                  <span className="text-green-600 font-bold">+{historyData.stock_balance?.total_in || 0}</span>
                  <span className="text-muted-foreground mx-1">(compras)</span>
                </span>
                <span className="text-muted-foreground">−</span>
                <span className="font-mono">
                  <span className="text-red-600 font-bold">{historyData.stock_balance?.total_out || 0}</span>
                  <span className="text-muted-foreground mx-1">(vendas)</span>
                </span>
                <span className="text-muted-foreground">=</span>
                <span className="font-mono">
                  <span className={`font-bold ${
                    historyData.stock_balance?.current < 0 ? 'text-red-600' : 
                    historyData.stock_balance?.current === 0 ? 'text-amber-600' : 'text-blue-600'
                  }`}>
                    {historyData.stock_balance?.current || 0}
                  </span>
                  <span className="text-muted-foreground mx-1">(estoque)</span>
                </span>
              </div>

              {/* Tabs */}
              <Tabs defaultValue="sales" className="flex-1 flex flex-col overflow-hidden">
                <TabsList className="w-full justify-start">
                  <TabsTrigger value="sales" className="flex items-center gap-1">
                    <ShoppingCart className="w-4 h-4" />
                    Vendas ({historyData.total_sales || 0})
                  </TabsTrigger>
                  <TabsTrigger value="purchases" className="flex items-center gap-1">
                    <FileText className="w-4 h-4" />
                    Compras ({historyData.purchases?.length || 0})
                  </TabsTrigger>
                  <TabsTrigger value="daily" className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    Por Dia
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="sales" className="flex-1 overflow-auto mt-4">
                  {historyData.sales?.length > 0 ? (
                    <table className="data-table text-sm">
                      <thead className="sticky top-0 bg-white">
                        <tr>
                          <th>Data/Hora</th>
                          <th>Transação</th>
                          <th>Qtd</th>
                          <th>Preço Un.</th>
                          <th>Total</th>
                          <th>Estoque Após</th>
                          <th>Cliente</th>
                        </tr>
                      </thead>
                      <tbody>
                        {historyData.sales.map((sale, idx) => (
                          <tr key={idx}>
                            <td className="text-xs">{formatDateTime(sale.date)}</td>
                            <td className="font-mono text-xs">{sale.transaction_id}</td>
                            <td className="font-mono text-red-600">-{sale.quantity}</td>
                            <td className="font-mono">{formatCurrency(sale.sale_price)}</td>
                            <td className="font-mono font-medium">{formatCurrency(sale.total)}</td>
                            <td className={`font-mono font-medium ${
                              sale.stock_after < 0 ? 'text-red-600' : 
                              sale.stock_after === 0 ? 'text-amber-600' : 'text-green-600'
                            }`}>
                              {sale.stock_after}
                            </td>
                            <td className="text-xs truncate max-w-[120px]" title={sale.customer}>
                              {sale.customer || '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <div className="py-8 text-center text-muted-foreground">
                      <ShoppingCart className="w-8 h-8 mx-auto mb-2 opacity-30" />
                      Nenhuma venda registrada
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="purchases" className="flex-1 overflow-auto mt-4">
                  {historyData.purchases?.length > 0 ? (
                    <table className="data-table text-sm">
                      <thead className="sticky top-0 bg-white">
                        <tr>
                          <th>Data Emissão</th>
                          <th>Fornecedor</th>
                          <th>Qtd</th>
                          <th>Custo Un.</th>
                          <th>Chave NF</th>
                        </tr>
                      </thead>
                      <tbody>
                        {historyData.purchases.map((purchase, idx) => (
                          <tr key={idx}>
                            <td>{formatDate(purchase.date)}</td>
                            <td className="max-w-[200px] truncate" title={purchase.supplier}>
                              {purchase.supplier}
                            </td>
                            <td className="font-mono font-medium text-green-700">+{purchase.quantity}</td>
                            <td className="font-mono">{formatCurrency(purchase.cost_price)}</td>
                            <td className="font-mono text-xs max-w-[150px] truncate" title={purchase.nf_key}>
                              {purchase.nf_key}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <div className="py-8 text-center text-muted-foreground">
                      <FileText className="w-8 h-8 mx-auto mb-2 opacity-30" />
                      Nenhuma compra registrada
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="daily" className="flex-1 overflow-auto mt-4">
                  {historyData.daily_sales?.length > 0 ? (
                    <table className="data-table text-sm">
                      <thead className="sticky top-0 bg-white">
                        <tr>
                          <th>Data</th>
                          <th>Vendas</th>
                          <th>Qtd Total</th>
                          <th>Receita</th>
                        </tr>
                      </thead>
                      <tbody>
                        {historyData.daily_sales.map((day, idx) => (
                          <tr key={idx}>
                            <td>{formatDate(day.date)}</td>
                            <td className="font-mono">{day.count}</td>
                            <td className="font-mono font-medium">{day.quantity}</td>
                            <td className="font-mono">{formatCurrency(day.revenue)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <div className="py-8 text-center text-muted-foreground">
                      <Calendar className="w-8 h-8 mx-auto mb-2 opacity-30" />
                      Nenhum dado diário disponível
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            </div>
          ) : null}

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => { setHistoryProduct(null); setHistoryData(null); }}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={!!editProduct} onOpenChange={() => setEditProduct(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editar Produto</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium">Nome</label>
              <Input
                value={editForm.name || ''}
                onChange={(e) => setEditForm({...editForm, name: e.target.value})}
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="text-sm font-medium">Quantidade</label>
                <Input
                  type="number"
                  value={editForm.quantity || 0}
                  onChange={(e) => setEditForm({...editForm, quantity: parseInt(e.target.value) || 0})}
                />
              </div>
              <div>
                <label className="text-sm font-medium">Preço Custo</label>
                <Input
                  type="number"
                  step="0.01"
                  value={editForm.cost_price || 0}
                  onChange={(e) => setEditForm({...editForm, cost_price: parseFloat(e.target.value) || 0})}
                />
              </div>
              <div>
                <label className="text-sm font-medium">Preço Venda</label>
                <Input
                  type="number"
                  step="0.01"
                  value={editForm.sale_price || 0}
                  onChange={(e) => setEditForm({...editForm, sale_price: parseFloat(e.target.value) || 0})}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditProduct(null)}>
              Cancelar
            </Button>
            <Button onClick={handleSaveEdit}>
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Stock;
